/**
 * Original-language byline tag for translated comments.
 *
 * `useOriginalLanguage` resolves the language flag to surface in the byline of
 * a comment: it returns the comment's original ISO locale whenever the silo is
 * multilingual and the language is configured for the current silo, regardless
 * of whether it matches the user's display language.
 */

import type { IsoLocale } from '@repo/shared/locale';
import { createThemedStyles } from '@repo/theme/styles';
import { LanguageFlag } from 'component/language-flag';
import { useTranslation } from 'component/translation';
import { View } from 'react-native';
import { useIsMultiLanguageSilo, useLanguageFlagCountryPrefix } from 'util/content-translation';
import { languageDefinitions } from 'util/locale';
import { useIsLanguageAvailable } from 'util/siloLanguages';

/** Returns the original ISO locale to tag, or null if no tag should be shown.
 *  Only languages configured for the current silo are supported — comments in
 *  other languages get no tag. */
export function useOriginalLanguage(originalLocale: IsoLocale | null | undefined): IsoLocale | null {
    const multiLanguageEnabled = useIsMultiLanguageSilo();
    const isAvailable = useIsLanguageAvailable(originalLocale);

    if (!multiLanguageEnabled || !originalLocale) return null;
    if (!isAvailable) return null;

    return originalLocale;
}

export function OriginalLanguageTag({ originalLanguage }: { originalLanguage: IsoLocale | null | undefined }) {
    const language = useOriginalLanguage(originalLanguage);
    const languageLabel = useTranslation(language ? languageDefinitions[language].label : null);
    const countryPrefix = useLanguageFlagCountryPrefix();
    const s = useOriginalLanguageTagStyles();
    if (!language) return null;

    return (
        <View accessibilityLabel={languageLabel ?? undefined} style={s.tag}>
            <LanguageFlag language={language} showSwissPrefix={countryPrefix === 'CH' ? true : undefined} size={12} />
        </View>
    );
}

const useOriginalLanguageTagStyles = createThemedStyles((tokens) => ({
    tag: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: tokens.colors.colorTagDefault,
        borderRadius: 32,
        height: 20,
        paddingHorizontal: 6,
        gap: 2,
        alignSelf: 'flex-start',
    },
}));
